import {
  isLadderEligible,
  quoteSheetLadder,
  type SheetLadderQuote,
  type SheetLadderSubject,
  type SheetLadderTier,
} from "./sheet-ladder";
import type { SizeId, SingleStickerConfiguration } from "./types";

type PricingConfiguration = Pick<SingleStickerConfiguration, "size" | "quantity">;
export interface SizePriceEntry {
  unitPrice: number;
  /**
   * Shopify's `compareAtPrice` for the variant, in öre — the pre-sale price to
   * strike through. `null` when the variant isn't on sale.
   */
  compareAtUnitPrice: number | null;
  currency: string;
}

export type SizePriceMap = Record<string, SizePriceEntry>;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * All amounts in öre (smallest SEK unit).
 * VAT is excluded — deferred to M8 or Shopify-side handling.
 *
 * The app predicts, Shopify charges (DEC-0036). Every number here comes from
 * Shopify: sales as a reduced `price` plus a higher `compareAtPrice`, and the
 * cart-wide sheet ladder as automatic discounts read over the Admin API. The
 * app never *decides* a discount — it only computes what Shopify's own
 * definitions will charge (`domain/sheet-ladder.ts`), and the add-to-cart path
 * reconciles that prediction against the cart Shopify built. Campaigns and
 * ladder steps are configured in Shopify, not in this file.
 */
export interface PriceResult {
  /** Shopify's live per-sheet variant price, before the ladder. */
  unitPrice: number;
  /** Struck-through per-sheet price, or `null` when not on sale. */
  compareAtUnitPrice: number | null;
  /**
   * What the sheets being added cost: `ladder.total`, i.e.
   * `unitPrice × quantity` less the sheet-ladder discount they earn given the
   * cart. Without a ladder, exactly `unitPrice × quantity`.
   */
  total: number;
  /** Struck-through campaign total (`compareAtUnitPrice × quantity`), or `null` when not on sale. */
  compareAtTotal: number | null;
  /** Whole-percent campaign saving (25 for 25% off), or `null` when not on sale. */
  discountPercent: number | null;
  currency: string;
  /**
   * The sheet-ladder quote for these sheets: per-sheet prices, ordinary total,
   * savings, next-sheet price and cap. Always present; with no ladder it is
   * all ordinary prices and zero savings.
   */
  ladder: SheetLadderQuote;
}

/** Cart context for the sheet ladder. Omit for a ladder-free price. */
export interface PriceLadderContext {
  tiers: readonly SheetLadderTier[];
  /** Eligible sheets already in the cart, excluding the line being edited. */
  sheetsInCart: number;
}

const NO_LADDER: PriceLadderContext = { tiers: [], sheetsInCart: 0 };

/**
 * The ladder input a sheet prices with: `ladder` when the sheet counts toward
 * the ladder, `undefined` when it does not. Legacy single sizes (reachable
 * through edit and reorder links) sit outside the ladder's discounts, so they
 * price without it. The one gate the configurator's live price and its
 * add-to-cart popup hint share.
 */
export function ladderContextFor<T>(subject: SheetLadderSubject, ladder: T): T | undefined {
  return isLadderEligible(subject) ? ladder : undefined;
}

// ---------------------------------------------------------------------------
// calculatePrice
// ---------------------------------------------------------------------------

/**
 * Pure function — same input always produces the same output.
 * Throws if the configuration references an unknown size ID or missing size pricing.
 * `config.quantity` is interpreted as physical sheet count.
 */
export function calculatePrice(
  config: PricingConfiguration,
  sizePrices: SizePriceMap,
  ladderContext: PriceLadderContext = NO_LADDER,
): PriceResult {
  const sizePrice = sizePrices[config.size as SizeId];
  if (!sizePrice) {
    throw new Error(`Unknown size id: "${config.size}"`);
  }

  const unitPrice = sizePrice.unitPrice;

  // Only treat compare-at as a sale when it is strictly higher than the price
  // being charged. A merchandising slip that leaves compare-at at or below the
  // price must not render as a struck-through "-0%" badge.
  const compareAtUnitPrice =
    sizePrice.compareAtUnitPrice !== null && sizePrice.compareAtUnitPrice > unitPrice
      ? sizePrice.compareAtUnitPrice
      : null;

  // The ladder discounts the live price, never compare-at: Shopify takes the
  // automatic discount off what it charges, and a campaign's struck price
  // stays the pre-campaign figure.
  const ladder = quoteSheetLadder({
    tiers: ladderContext.tiers,
    sheetsInCart: ladderContext.sheetsInCart,
    sheetsAdding: config.quantity,
    baseUnitPrice: unitPrice,
  });

  return {
    unitPrice,
    compareAtUnitPrice,
    total: ladder.total,
    compareAtTotal:
      compareAtUnitPrice === null ? null : compareAtUnitPrice * config.quantity,
    discountPercent:
      compareAtUnitPrice === null
        ? null
        : Math.round(((compareAtUnitPrice - unitPrice) / compareAtUnitPrice) * 100),
    currency: sizePrice.currency,
    ladder,
  };
}

// ---------------------------------------------------------------------------
// formatPrice
// ---------------------------------------------------------------------------

const _formatter = new Intl.NumberFormat("sv-SE", {
  style: "currency",
  currency: "SEK",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** Formats an amount in öre to a Swedish locale currency string, e.g. "29,00 kr". */
export function formatPrice(amountInOre: number): string {
  return _formatter.format(amountInOre / 100);
}

const _wholeFormatter = new Intl.NumberFormat("sv-SE", {
  style: "currency",
  currency: "SEK",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

/**
 * Like `formatPrice`, but without öre when the amount is whole kronor —
 * "199 kr" rather than "199,00 kr" — for compact inline copy such as the
 * sheet-ladder line. Fractional amounts fall back to `formatPrice`, so money
 * is never rounded away.
 */
export function formatPriceCompact(amountInOre: number): string {
  return amountInOre % 100 === 0
    ? _wholeFormatter.format(amountInOre / 100)
    : formatPrice(amountInOre);
}
